import React, { useState, useEffect } from "react";
import {
  EyeIcon,
  EyeOffIcon,
  EmailIcon,
  LockIcon,
  AlertCircleIcon,
  ProfileIcon,
} from "../../assets/icons";
import {
  validateForm,
  validateFieldRealTime,
  clearFieldError,
} from "../../utils/validation";
import { Api } from "../../network/api";
import { setProfile } from "../../redux/slice/globalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  const [userData, setUserData] = useState({
    id: "",
    name: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const { profile } = useSelector((state) => state.global);

  const inputClass = `
    w-full px-5 py-4 border-2 rounded-2xl transition-all duration-300
    bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500
    border-gray-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20
    focus:outline-none
  `;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await Api.getProfile();
        const profileData = response.data;

        dispatch(setProfile(profileData));

        setUserData((prev) => ({
          ...prev,
          id: profileData.id,
          name: profileData.name,
          email: profileData.email,
        }));

        setOriginalData(profileData);
      } catch (error) {
        console.error("Failed to load profile", error);
        showNotification("error", "Failed to load profile data");
      }
    };

    fetchProfile();
  }, []);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(
      () => setNotification({ show: false, type: "", message: "" }),
      4000
    );
  };

  const handleInputChange = (field, value) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));

    const validation = validateFieldRealTime(field, value, userData);
    if (validation.isValid) {
      setErrors((prev) => clearFieldError(prev, field));
    } else {
      setErrors((prev) => ({
        ...prev,
        [field]: validation.message,
      }));
    }
  };
  const validateFormData = () => {
    const validation = validateForm(userData, ["name", "email"]);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleUpdate = async () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    if (!validateFormData()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: userData.name.trim(),
        email: userData.email.trim(),
        current_password: userData.currentPassword,
      };

      if (userData.newPassword) {
        payload.new_password = userData.newPassword;
      }

      const response = await Api.updateProfile(userData.id, payload);

      try {
        const profileResponse = await Api.getProfile();
        const freshProfileData = profileResponse.data;
        dispatch(setProfile(freshProfileData));
        setOriginalData(freshProfileData);
        setUserData((prev) => ({
          ...prev,
          id: freshProfileData.id,
          name: freshProfileData.name,
          email: freshProfileData.email,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));

        setIsEditing(false);
        showNotification("success", "Profile updated successfully!");
      } catch (profileError) {
        console.error("Failed to fetch updated profile:", profileError);
        dispatch(setProfile(response.data));
        setOriginalData(response.data);
        setUserData((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));
        setIsEditing(false);
        showNotification("success", "Profile updated successfully!");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to update profile. Please try again.";
      showNotification("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (isEditing) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to leave?"
        )
      ) {
        setIsModalOpen(false);
        navigate(-1);
      }
    } else {
      setIsModalOpen(false);
      navigate(-1);
    }
  };

  if (loading && !originalData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
        <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl rounded-3xl p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            <span className="text-gray-700 font-medium">
              Loading profile...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 p-4">
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-2xl w-full max-w-lg mx-auto animate-fadeInUp rounded-3xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-red-600 to-red-800 px-8 py-6 text-center">
              <h2 className="text-2xl font-bold text-white">
                Profile Settings
              </h2>
              <p className="text-red-100 text-sm font-medium">
                Manage your account information
              </p>
            </div>

            {notification.show && (
              <div
                className={`mx-6 mt-6 p-4 rounded-xl border-l-4 ${
                  notification.type === "success"
                    ? "bg-green-50 border-green-400 text-green-700"
                    : "bg-red-50 border-red-400 text-red-700"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <AlertCircleIcon width="20" height="20" />
                  <span className="font-medium">{notification.message}</span>
                </div>
              </div>
            )}

            <div className="px-8 py-8 space-y-8 flex-1 overflow-y-auto scrollbar-hide">
              <div className="space-y-4">
                <label className="text-lg font-semibold text-gray-900">
                  Full Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={userData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    disabled={!isEditing}
                    className={`${inputClass} pl-12 ${
                      isEditing ? "" : "border-gray-200 bg-gray-50"
                    } ${
                      errors.name ? "border-red-300 focus:border-red-500" : ""
                    }`}
                    placeholder="Enter your full name"
                  />
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <ProfileIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                {errors.name && (
                  <p className="text-sm text-red-600 flex items-center space-x-2">
                    <AlertCircleIcon width="16" height="16" />
                    <span>{errors.name}</span>
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <label className="text-lg font-semibold text-gray-900">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={userData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    disabled={!isEditing}
                    className={`${inputClass} pl-12 ${
                      isEditing ? "" : "border-gray-200 bg-gray-50"
                    } ${
                      errors.email ? "border-red-300 focus:border-red-500" : ""
                    }`}
                    placeholder="Enter your email address"
                  />
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <EmailIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                {errors.email && (
                  <p className="text-sm text-red-600 flex items-center space-x-2">
                    <AlertCircleIcon width="16" height="16" />
                    <span>{errors.email}</span>
                  </p>
                )}
              </div>

              {isEditing && (
                <>
                  <div className="space-y-4">
                    <label className="text-lg font-semibold text-gray-900">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={userData.currentPassword}
                        onChange={(e) =>
                          handleInputChange("currentPassword", e.target.value)
                        }
                        className={`${inputClass} pl-12 pr-12 ${
                          errors.currentPassword
                            ? "border-red-300 focus:border-red-500"
                            : ""
                        }`}
                        placeholder="Enter current password"
                      />
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <LockIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        >
                          {showPassword ? (
                            <EyeOffIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="text-lg font-semibold text-gray-900">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={userData.newPassword}
                        onChange={(e) =>
                          handleInputChange("newPassword", e.target.value)
                        }
                        className={`${inputClass} pl-12 pr-12 ${
                          errors.newPassword
                            ? "border-red-300 focus:border-red-500"
                            : ""
                        }`}
                        placeholder="Enter new password (optional)"
                      />
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <LockIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        >
                          {showNewPassword ? (
                            <EyeOffIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="text-lg font-semibold text-gray-900">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={userData.confirmPassword}
                        onChange={(e) =>
                          handleInputChange("confirmPassword", e.target.value)
                        }
                        className={`${inputClass} pl-12 pr-12 ${
                          errors.confirmPassword
                            ? "border-red-300 focus:border-red-500"
                            : ""
                        }`}
                        placeholder="Confirm new password"
                      />
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <LockIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        >
                          {showConfirmPassword ? (
                            <EyeOffIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end px-8 py-6 border-t border-gray-200 bg-gray-50/50">
              <button
                onClick={handleCloseModal}
                disabled={loading}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={loading}
                className="ml-4 px-6 py-3 rounded-2xl font-medium transition-all duration-200 bg-gradient-to-r from-red-600 to-red-800 text-white hover:from-red-700 hover:to-red-900 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? "Saving..."
                  : isEditing
                  ? "Save Changes"
                  : "Edit Profile"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
